import { Component, Input, forwardRef, OnInit, OnDestroy, OnChanges, SimpleChanges, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../services/global-environment';
import { Subject } from 'rxjs';
import { takeUntil, catchError, debounceTime } from 'rxjs/operators';
import { of, fromEvent, merge } from 'rxjs';
import { TranslatePipe } from '../../pipes/translate.pipe';

interface ApiData {
  [key: string]: any;
}

interface SelectedItem {
  item: ApiData;
  index: number;
}

interface ApiResponse {
  success: boolean;
  data: ApiData[];
}

interface SelectColumn {
  field: string;  // 資料庫欄位代碼
  alias: string;  // 顯示名稱
}

@Component({
  selector: 'app-form-dynamic-api-table',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="form-section">
      <div class="form-header">
        <label [class.required-field]="required">{{ label }}</label>
        <button
          type="button"
          class="collapse-toggle"
          (click)="toggleCollapse()"
          *ngIf="!loading && !hasError && (tableData.length > 0 || readOnlyMode)"
        >
          <i class="fas" [class.fa-chevron-up]="!isCollapsed" [class.fa-chevron-down]="isCollapsed"></i>
        </button>
      </div>

      <!-- 唯讀顯示模式 -->
      <div *ngIf="readOnlyMode && initialData && initialData.length > 0" class="readonly-display">
        <div class="readonly-summary">
          <i class="fas fa-table"></i>
          <span>{{ 'form.dynamic_api_table.selected_count' | translate:{ count: initialData.length } }}</span>
          <button type="button" class="btn btn-sm btn-outline-primary reselect-btn" (click)="enableEditMode()">
            <i class="fas fa-edit"></i>
            {{ 'form.dynamic_api_table.reselect' | translate }}
          </button>
        </div>
        <div class="readonly-items" *ngIf="!isCollapsed">
          <div *ngFor="let item of initialData; let i = index" class="readonly-item">
            <span class="item-index">{{ i + 1 }}.</span>
            <span class="item-content">{{ getItemSummary(item) }}</span>
          </div>
        </div>
      </div>

      <!-- 原本的選擇模式 -->
      <div *ngIf="!readOnlyMode">
        <!-- 選擇計數顯示 - 一直顯示 -->
        <div *ngIf="!loading && !hasError && tableData.length > 0" class="selection-info">
          <span class="selection-count">
            {{ 'form.dynamic_api_table.selected_items' | translate:{ selected: selectedItems.length, total: tableData.length } }}
            <span *ngIf="maxSelections" class="max-limit">
              {{ 'form.dynamic_api_table.max_selections' | translate:{ max: maxSelections } }}
            </span>
          </span>
        </div>

      <!-- 可收合的內容區域 -->
      <div class="collapsible-content" [class.collapsed]="isCollapsed">
        <!-- 載入狀態 -->
        <div *ngIf="loading" class="loading-container">
          <div class="loading-spinner">
            <i class="fas fa-spinner fa-spin"></i>
            {{ 'form.dynamic_api_table.loading' | translate }}
          </div>
        </div>

        <!-- 錯誤狀態 -->
        <div *ngIf="hasError && !loading" class="error-container">
          <div class="error-message">
            <i class="fas fa-exclamation-triangle"></i>
            {{ 'form.dynamic_api_table.load_data_failed' | translate }}
          </div>
          <button class="btn btn-sm btn-primary" (click)="reloadData()">
            <i class="fas fa-redo"></i> {{ 'form.dynamic_api_table.reload' | translate }}
          </button>
        </div>

        <!-- 空資料狀態 -->
        <div *ngIf="!loading && !hasError && tableData.length === 0" class="empty-state">
          <i class="fas fa-inbox"></i>
          <p>{{ 'form.dynamic_api_table.no_data' | translate }}</p>
        </div>

        <!-- 表格內容 -->
        <div *ngIf="!loading && !hasError && tableData.length > 0" class="table-container">
          <div class="table-wrapper">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="select-column">
                    <input
                      type="checkbox"
                      [checked]="isAllSelected()"
                      [indeterminate]="isPartiallySelected()"
                      (change)="toggleSelectAll($event)"
                      [disabled]="maxSelections && maxSelections === selectedItems.length && !isAllSelected()"
                    >
                    <span class="select-all-label">{{ 'form.dynamic_api_table.select_all' | translate }}</span>
                  </th>
                  <th *ngFor="let column of tableColumns" [class]="'column-' + column">
                    {{ getColumnDisplayName(column) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let item of tableData; let i = index"
                    [class.selected-row]="isItemSelected(item, i)"
                    [class.clickable-row]="!isRowDisabled(item, i)"
                    (click)="onRowClick(item, i, $event)">
                  <td class="select-column" (click)="$event.stopPropagation()">
                    <input
                      type="checkbox"
                      [checked]="isItemSelected(item, i)"
                      (change)="toggleItemSelection(item, i, $event)"
                      [disabled]="maxSelections && maxSelections === selectedItems.length && !isItemSelected(item, i)"
                    >
                  </td>
                  <td *ngFor="let column of tableColumns" [class]="'column-' + column">
                    {{ getDisplayValue(item[column]) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </div>

      <!-- 除錯面板 (僅在開發模式下顯示) -->
      <div *ngIf="debugMode" class="debug-panel">
        <div class="debug-header">
          <i class="fas fa-bug"></i> 除錯資訊 (表格元件)
        </div>
        <div class="debug-content">
          <div class="debug-item">
            <strong>Where 條件啟用:</strong> {{ enableWhereCondition }}
          </div>
          <div class="debug-item" *ngIf="enableWhereCondition">
            <strong>來源元件名稱:</strong> {{ whereSourceComponent }}
          </div>
          <div class="debug-item" *ngIf="enableWhereCondition">
            <strong>目標篩選欄位:</strong> {{ whereTargetField }}
          </div>
          <div class="debug-item" *ngIf="enableWhereCondition">
            <strong>目前來源值:</strong> {{ sourceValueCache }}
          </div>
          <div class="debug-item">
            <strong>表單 ID:</strong> {{ formId }}
          </div>
          <div class="debug-item">
            <strong>載入狀態:</strong> {{ loading ? '載入中' : '已完成' }}
          </div>
          <div class="debug-item">
            <strong>錯誤狀態:</strong> {{ hasError ? '有錯誤' : '正常' }}
          </div>
          <div class="debug-item">
            <strong>資料筆數:</strong> {{ tableData.length }}
          </div>
          <div class="debug-item">
            <strong>已選項目:</strong> {{ selectedItems.length }}
          </div>
        </div>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormDynamicApiTableComponent),
      multi: true
    }
  ],
  styleUrls: ['total-component.scss']
})
export class FormDynamicApiTableComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() formId: string = '';
  @Input() selectcols: SelectColumn[] = []; // 欄位設定陣列，包含 field 和 alias
  @Input() maxSelections?: number;
  @Input() editMode: boolean = false; // 新增：是否為編輯模式

  // Where 條件相關屬性
  @Input() enableWhereCondition: boolean = false;
  @Input() whereSourceComponent: string = '';
  @Input() whereTargetField: string = '';

  selectedItems: SelectedItem[] = [];
  tableData: ApiData[] = [];
  tableColumns: string[] = [];
  columnAliases: { [key: string]: string } = {}; // 欄位顯示名稱對照表
  loading: boolean = false;
  hasError: boolean = false;
  isCollapsed: boolean = false;
  
  // 新增：唯讀模式相關屬性
  readOnlyMode: boolean = false;
  initialData: any[] = [];
  originalValue: any = null;
  hasBeenEdited: boolean = false;

  private destroy$ = new Subject<void>();
  private apiUrl = environment.apiUrl;
  private cache: Map<string, ApiData[]> = new Map();
  public sourceValueCache: string = '';
  public debugMode: boolean = false; // 可在開發時設為 true

  // ControlValueAccessor callback 函數
  onChange = (value: any) => {};
  onTouched = () => {};

  constructor(private http: HttpClient, private elementRef: ElementRef) {}

  ngOnInit(): void {
    // 初始化 columnAliases，即使沒有載入資料也能使用
    if (this.selectcols && this.selectcols.length > 0) {
      this.columnAliases = {};
      this.selectcols.forEach(col => {
        // 設定多種可能的欄位名稱格式的別名
        this.columnAliases[col.field] = col.alias;
        this.columnAliases[`${col.field}.value`] = col.alias;
        if (col.field.includes('.value')) {
          this.columnAliases[col.field.replace('.value', '')] = col.alias;
        }
      });
    }
    
    if (this.formId && this.selectcols && this.selectcols.length > 0) {
      this.loadData();
    }

    // 如果啟用了 where 條件，設置監聽機制
    if (this.enableWhereCondition && this.whereSourceComponent) {
      this.setupWhereConditionListener();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // 當 selectcols 變更時，更新 columnAliases
    if (changes['selectcols'] && changes['selectcols'].currentValue) {
      this.columnAliases = {};
      const newSelectcols = changes['selectcols'].currentValue;
      if (newSelectcols && Array.isArray(newSelectcols)) {
        newSelectcols.forEach((col: SelectColumn) => {
          this.columnAliases[col.field] = col.alias;
          this.columnAliases[`${col.field}.value`] = col.alias;
          if (col.field.includes('.value')) {
            this.columnAliases[col.field.replace('.value', '')] = col.alias;
          }
        });
      }
    }
    
    // 當 formId 或 selectcols 變更時，重新載入資料
    if ((changes['formId'] && !changes['formId'].firstChange && changes['formId'].currentValue) ||
        (changes['selectcols'] && !changes['selectcols'].firstChange && changes['selectcols'].currentValue)) {
      this.loadData();
    }

    // 當 where 條件相關屬性變更時，重新設置監聽或載入資料
    if (changes['enableWhereCondition'] || changes['whereSourceComponent'] || changes['whereTargetField']) {
      if (this.enableWhereCondition && this.whereSourceComponent) {
        this.setupWhereConditionListener();
      } else {
        this.loadData();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    if (!this.formId || !this.selectcols || this.selectcols.length === 0) {
      console.warn('FormDynamicApiTableComponent: formId and selectcols are required');
      return;
    }

    // 取得 where 條件的值
    let whereConditionValue = '';
    if (this.enableWhereCondition && this.whereSourceComponent && this.whereTargetField) {
      whereConditionValue = this.getSourceComponentValue();
      if (!whereConditionValue) {
        if (this.debugMode) {
          console.log('FormDynamicApiTableComponent: Waiting for source component value');
        }
        this.tableData = [];
        this.tableColumns = [];
        return;
      }
    }

    // 檢查快取 - 使用 companyid + formId + selectcols + where 條件作為快取鍵
    const companyid = environment.companyid;
    const selectcolsKey = this.selectcols.map(col => `${col.field}:${col.alias}`).join(';');
    const whereKey = this.enableWhereCondition ? `_where_${this.whereSourceComponent}_${this.whereTargetField}_${whereConditionValue}` : '';
    const cacheKey = `${companyid}_${this.formId}_${selectcolsKey}${whereKey}`;

    if (this.cache.has(cacheKey)) {
      this.tableData = this.cache.get(cacheKey) || [];
      this.setupTableColumns();
      return;
    }

    if (!companyid) {
      console.error('FormDynamicApiTableComponent: companyid is not available in environment');
      this.hasError = true;
      return;
    }

    this.loading = true;
    this.hasError = false;
    this.tableData = [];
    this.tableColumns = [];

    const url = `${this.apiUrl}/api/dynamic-data/query`;
    const requestBody: any = {
      companyid: companyid,
      containername: 'form-container',
      idField: 'formid', // 使用 formid 作為 ID 欄位
      idValue: this.formId,
      selectcols: this.selectcols // 傳送完整的欄位設定陣列
    };

    // 加入 where 條件
    if (this.enableWhereCondition && this.whereTargetField && whereConditionValue) {
      requestBody.where = {
        field: this.whereTargetField,
        value: whereConditionValue
      };

      if (this.debugMode) {
        console.log('FormDynamicApiTableComponent: Adding where condition:', requestBody.where);
      }
    }

    if (this.debugMode) {
      console.log('FormDynamicApiTableComponent API Request:', requestBody);
    }

    this.http.post<ApiResponse>(url, requestBody)
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Failed to load dynamic data:', error);
          this.hasError = true;
          return of({ success: false, data: [] });
        })
      )
      .subscribe(response => {
        if (this.debugMode) {
          console.log('FormDynamicApiTableComponent API Response:', response);
          console.log('SelectCols setting:', this.selectcols);
        }
        this.loading = false;

        if (response.success && Array.isArray(response.data)) {
          this.tableData = response.data;
          if (this.debugMode) {
            console.log('Table data loaded:', this.tableData);
          }
          this.setupTableColumns();
          // 快取結果 - 使用完整的快取鍵
          this.cache.set(cacheKey, this.tableData);
        } else {
          this.hasError = true;
          console.error('Invalid API response format:', response);
        }
      });
  }

  private setupTableColumns(): void {
    if (this.tableData.length > 0) {
      // 收集所有資料列的欄位名稱，確保不會遺漏任何欄位
      const allColumns = new Set<string>();
      this.tableData.forEach(item => {
        Object.keys(item).forEach(key => {
          allColumns.add(key);
        });
      });
      this.tableColumns = Array.from(allColumns);

      // 建立返回欄位與設定別名的對照表
      this.columnAliases = {};
      if (this.selectcols && this.selectcols.length > 0) {
        // 根據 selectcols 設定的 field 欄位來建立對照表
        this.selectcols.forEach(selectCol => {
          const matchingColumn = this.tableColumns.find(column =>
            column === selectCol.field ||
            column === `${selectCol.field}.value` ||
            column.includes(selectCol.field)
          );

          if (matchingColumn && selectCol.alias) {
            this.columnAliases[matchingColumn] = selectCol.alias;
          }
        });

        // 如果沒有設定 selectcols，顯示所有欄位
        // 或者如果有額外的欄位（如 parent_formid），也要顯示
        this.tableColumns.forEach(column => {
          if (!this.columnAliases[column]) {
            // 為沒有別名的欄位提供預設顯示名稱
            if (column === 'parent_formid') {
              this.columnAliases[column] = '父表單ID';
            } else if (column.endsWith('.value')) {
              // 移除 .value 後綴作為顯示名稱
              this.columnAliases[column] = column.replace('.value', '');
            } else {
              this.columnAliases[column] = column;
            }
          }
        });
      } else {
        // 如果沒有 selectcols 設定，為所有欄位提供預設顯示名稱
        this.tableColumns.forEach(column => {
          if (column === 'parent_formid') {
            this.columnAliases[column] = '父表單ID';
          } else if (column.endsWith('.value')) {
            this.columnAliases[column] = column.replace('.value', '');
          } else {
            this.columnAliases[column] = column;
          }
        });
      }

      console.log('Table columns:', this.tableColumns);
      console.log('Column aliases:', this.columnAliases);
      console.log('SelectCols configuration:', this.selectcols);
    }
  }

  // 全選/反選功能
  isAllSelected(): boolean {
    return this.tableData.length > 0 && this.selectedItems.length === this.tableData.length;
  }

  isPartiallySelected(): boolean {
    return this.selectedItems.length > 0 && this.selectedItems.length < this.tableData.length;
  }

  toggleSelectAll(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      // 全選 - 考慮最大選取限制
      const itemsToSelect = this.maxSelections ?
        this.tableData.slice(0, this.maxSelections) :
        this.tableData;

      this.selectedItems = itemsToSelect.map((item, index) => ({ item, index }));
    } else {
      // 全不選
      this.selectedItems = [];
    }

    this.onSelectionChange();
  }

  // 列點擊功能
  onRowClick(item: ApiData, index: number, event: Event): void {
    // 如果該項目已被禁用，則不執行任何操作
    if (this.isRowDisabled(item, index)) {
      return;
    }

    // 切換選中狀態
    const isCurrentlySelected = this.isItemSelected(item, index);

    if (!isCurrentlySelected) {
      // 要選中該項目，檢查是否超過最大選擇限制
      if (!this.maxSelections || this.selectedItems.length < this.maxSelections) {
        this.selectedItems = [...this.selectedItems, { item, index }];
      }
    } else {
      // 要取消選中該項目
      this.selectedItems = this.selectedItems.filter(selected => selected.index !== index);
    }

    this.onSelectionChange();
  }

  // 檢查列是否被禁用
  isRowDisabled(item: ApiData, index: number): boolean {
    // 如果已達到最大選擇限制且該項目未被選中，則禁用
    return this.maxSelections !== undefined &&
           this.maxSelections === this.selectedItems.length &&
           !this.isItemSelected(item, index);
  }

  // 單項選擇功能
  isItemSelected(item: ApiData, index: number): boolean {
    return this.selectedItems.some(selected => selected.index === index);
  }

  toggleItemSelection(item: ApiData, index: number, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      // 檢查是否超過最大選擇限制
      if (!this.maxSelections || this.selectedItems.length < this.maxSelections) {
        this.selectedItems = [...this.selectedItems, { item, index }];
      }
    } else {
      this.selectedItems = this.selectedItems.filter(selected => selected.index !== index);
    }

    this.onSelectionChange();
  }

  private compareItems(item1: ApiData, item2: ApiData): boolean {
    // 如果有 id 欄位，優先使用 id 比較
    if (item1['id'] && item2['id']) {
      return item1['id'] === item2['id'];
    }
    // 否則使用 JSON 字串比較（簡單但可能不夠精確）
    return JSON.stringify(item1) === JSON.stringify(item2);
  }

  private onSelectionChange(): void {
    // 回傳選中項目的資料（不包含 index）
    const selectedData = this.selectedItems.map(selected => selected.item);
    this.onChange(selectedData);
    this.onTouched();
  }

  // 顯示相關的輔助方法
  getColumnDisplayName(column: string): string {
    // 優先使用設定的別名，否則使用原欄位名稱
    return this.columnAliases[column] || column;
  }

  getDisplayValue(value: any): string {
    if (value === null || value === undefined) {
      return '-';
    }
    
    // 如果是物件，遞迴地尋找 value 屬性
    if (typeof value === 'object') {
      // 處理陣列的情況
      if (Array.isArray(value)) {
        // 如果是陣列，將每個元素轉換後用逗號連接
        return value.map(item => this.getDisplayValue(item)).join(', ');
      }
      
      // 處理物件的情況
      // 優先尋找 value 屬性
      if ('value' in value) {
        // 遞迴處理 value 屬性
        return this.getDisplayValue(value.value);
      }
      
      // 如果沒有 value 屬性，嘗試其他常見的顯示欄位
      if ('label' in value) {
        return this.getDisplayValue(value.label);
      }
      
      if ('name' in value) {
        return this.getDisplayValue(value.name);
      }
      
      if ('text' in value) {
        return this.getDisplayValue(value.text);
      }
      
      if ('displayValue' in value) {
        return this.getDisplayValue(value.displayValue);
      }
      
      // 如果物件有 key 和 value 的組合（常見於選項格式）
      if ('key' in value && 'value' in value) {
        // 優先顯示 value，如果 value 還是物件則遞迴處理
        return this.getDisplayValue(value.value);
      }
      
      // 如果物件只有一個屬性，直接取該屬性的值
      const keys = Object.keys(value);
      if (keys.length === 1) {
        return this.getDisplayValue(value[keys[0]]);
      }
      
      // 如果物件有多個屬性，嘗試組合顯示有意義的內容
      if (keys.length > 0) {
        // 過濾掉一些系統欄位
        const displayKeys = keys.filter(key => 
          !['id', '_id', 'createdAt', 'updatedAt', '__v'].includes(key)
        );
        
        if (displayKeys.length > 0) {
          // 如果還有其他欄位，組合顯示前幾個
          const displayValues = displayKeys
            .slice(0, 2) // 最多顯示2個欄位
            .map(key => {
              const val = this.getDisplayValue(value[key]);
              return val !== '-' ? val : null;
            })
            .filter(val => val !== null);
          
          if (displayValues.length > 0) {
            return displayValues.join(' - ');
          }
        }
      }
      
      // 如果都沒有找到合適的顯示值，返回物件的字串表示
      // 但限制長度避免顯示過長
      const jsonStr = JSON.stringify(value);
      if (jsonStr.length > 50) {
        return jsonStr.substring(0, 47) + '...';
      }
      return jsonStr;
    }
    
    // 如果是基本型別，直接轉換為字串
    return String(value);
  }

  // ControlValueAccessor 實作
  writeValue(value: any): void {
    console.log('FormDynamicApiTableComponent writeValue:', { 
      value, 
      editMode: this.editMode,
      selectcols: this.selectcols,
      columnAliases: this.columnAliases 
    });
    
    if (Array.isArray(value) && value.length > 0) {
      // 在編輯模式下，設定唯讀顯示
      if (this.editMode) {
        this.readOnlyMode = true;
        this.initialData = value;
        this.originalValue = value;
        this.selectedItems = [];
        console.log('Set initialData for readonly display:', this.initialData);
      } else {
        // 需要找到對應的索引來重建 selectedItems
        this.selectedItems = [];
        value.forEach(valueItem => {
          const foundIndex = this.tableData.findIndex(tableItem =>
            this.compareItems(tableItem, valueItem)
          );
          if (foundIndex !== -1) {
            this.selectedItems.push({ item: this.tableData[foundIndex], index: foundIndex });
          }
        });
      }
    } else {
      this.selectedItems = [];
    }
  }
  
  // 新增：啟用編輯模式
  enableEditMode(): void {
    this.readOnlyMode = false;
    this.hasBeenEdited = true;
    // 載入表格資料
    if (!this.tableData.length) {
      this.loadData();
    }
  }
  
  // 新增：取得項目摘要顯示
  getItemSummary(item: any): string {
    // 優先使用配置的欄位別名來顯示
    const summary: string[] = [];
    
    // 處理所有欄位，不管是否有 selectcols 配置
    const keys = Object.keys(item);
    let displayedCount = 0;
    const maxDisplay = 3; // 最多顯示3個欄位
    
    for (const key of keys) {
      if (displayedCount >= maxDisplay) break;
      
      const value = item[key];
      if (value !== undefined && value !== null && value !== '') {
        let label = key;
        
        // 如果 key 已經是中文標籤（從資料庫儲存的格式），直接使用
        if (/[\u4e00-\u9fff]/.test(key)) {
          label = key;
        } 
        // 否則嘗試從 selectcols 找對應的別名
        else if (this.selectcols && this.selectcols.length > 0) {
          const matchingCol = this.selectcols.find(col => 
            col.field === key || 
            `${col.field}.value` === key ||
            col.field === key.replace('.value', '')
          );
          if (matchingCol) {
            label = matchingCol.alias;
          }
        }
        // 或從 columnAliases 找
        else if (this.columnAliases[key]) {
          label = this.columnAliases[key];
        }
        
        // 使用 getDisplayValue 來處理值，確保巢狀物件也能正確顯示
        const displayValue = this.getDisplayValue(value);
        if (displayValue && displayValue !== '-') {
          summary.push(`${label}: ${displayValue}`);
          displayedCount++;
        }
      }
    }
    
    // 如果沒有找到任何有效值，返回簡單的描述
    if (summary.length === 0) {
      return '項目資料';
    }
    
    return summary.join(', ');
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 可以在這裡處理禁用狀態
  }

  // 收合功能
  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  // 重新載入資料（如果需要的話）
  reloadData(): void {
    const companyid = environment.companyid;
    const selectcolsKey = this.selectcols.map(col => `${col.field}:${col.alias}`).join(';');
    const cacheKey = `${companyid}_${this.formId}_${selectcolsKey}`;
    this.cache.delete(cacheKey);
    this.loadData();
  }

  // Where 條件相關方法
  private setupWhereConditionListener(): void {
    if (!this.enableWhereCondition || !this.whereSourceComponent) {
      return;
    }

    if (this.debugMode) {
      console.log('FormDynamicApiTableComponent: Setting up where condition listener for:', this.whereSourceComponent);
    }

    // 設置多種事件監聽
    const events = ['change', 'input', 'blur'];
    const eventObservables = events.map(eventType =>
      fromEvent(document, eventType).pipe(
        debounceTime(300)
      )
    );

    merge(...eventObservables)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const currentValue = this.getSourceComponentValue();
        if (currentValue !== this.sourceValueCache) {
          this.sourceValueCache = currentValue;
          if (this.debugMode) {
            console.log('FormDynamicApiTableComponent: Source component value changed:', currentValue);
          }
          this.loadData();
        }
      });

    // 初始載入
    setTimeout(() => {
      const initialValue = this.getSourceComponentValue();
      if (initialValue) {
        this.sourceValueCache = initialValue;
        this.loadData();
      }
    }, 100);
  }

  private getSourceComponentValue(): string {
    if (!this.whereSourceComponent) {
      return '';
    }

    // 嘗試多種方式獲取來源元件的值
    let sourceElement: HTMLElement | null = null;

    // 1. 嘗試通過表單控制項名稱
    const formElement = this.elementRef.nativeElement.closest('form');
    if (formElement) {
      sourceElement = formElement.querySelector(`[name="${this.whereSourceComponent}"]`);
    }

    // 2. 如果沒找到，嘗試通過 ID
    if (!sourceElement) {
      sourceElement = document.getElementById(this.whereSourceComponent);
    }

    // 3. 最後嘗試通過 querySelector
    if (!sourceElement) {
      sourceElement = document.querySelector(`[data-field-name="${this.whereSourceComponent}"]`);
    }

    if (sourceElement) {
      const inputElement = sourceElement as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      const value = inputElement.value || '';

      if (this.debugMode) {
        console.log(`FormDynamicApiTableComponent: Found source element [${this.whereSourceComponent}] with value:`, value);
      }

      return value;
    }

    if (this.debugMode) {
      console.warn(`FormDynamicApiTableComponent: Source component [${this.whereSourceComponent}] not found`);
    }

    return '';
  }
}
